<template>
	<Dialog :visible="true" style="width: 55vw" modal :closable="false">
		<template #header>
			<div class="flex justify-between w-full">
				<h1>{{ pageEditText }}</h1>
				<div class="flex gap-2">
					<Button id="btnBackToPRList" type="button" @click="emit('cancel')" class="btn-tiny">Cancel</Button>
					<Button id="btnSaveProject" class="btn-tiny" v-show="SessionInfo.hasRightsToScreen('Projects', PageAccess.Create)" @click="save()">
						Save
					</Button>
				</div>
			</div>
		</template>

		<I2FormRow>
			<I2FormField label="Sub Project Name">
				<InputText v-model="subProject.subProjectName" :class="{ 'p-invalid': saveAttempted && !subProject.subProjectName }" maxlength="200" />
			</I2FormField>
		</I2FormRow>
		<I2FormRow>
			<I2FormField label="Module">
				<!--<AutoComplete
					:multiple="true"
					v-model="selectedSkills"
					:suggestions="filteredSkills"
					@complete="searchSkill($event)"
					field="screenName"
					@item-select="checkIfSkillAreaIsPresent($event)"
					v-if="multipleSelection == true"
				/>:class="{ 'p-invalid': saveAttempted && selectedSkills == null }"
			@change="checkIfSkillAreaIsPresent($event)"-->
				<MultiSelect
					v-model="selectedSkills"
					:options="selectableScreens"
					optionLabel="screenName"
					optionValue="id"
					dataKey="id"
					display="chip"
					placeholder="Select module"
					selectedItemsOrder="order"
				/>
			</I2FormField>
		</I2FormRow>
		<I2FormRow>
			<I2FormField label="Sub Project Collaborators">
				<AutoComplete
					:multiple="true"
					v-model="selectedPersons"
					:suggestions="filteredPersons"
					:optionLabel="personLabel"
					optionValue="id"
					@complete="searchPerson($event)"
					:field="personLabel"
					@item-select="checkIfPersonIsPresent($event)"
				/>
			</I2FormField>
		</I2FormRow>
		<I2FormRow>
			<I2FormField label="Sub Project Comments">
				<Textarea v-model="subProject.comments" rows="4" />
			</I2FormField>
		</I2FormRow>
		<I2FormRow right> </I2FormRow
	></Dialog>
	<Toast position="top-right" />
</template>
<script setup lang="ts">
import { ref, onMounted, reactive, PropType } from "vue"
import {
	ApiClient,
	Project,
	Person,
	SubProjectCollaboratorPut,
	SubProject,
	SubProjectDefaultScreen,
	Screen,
	ScreenTabs,
	SubProjectForTable,
} from "../../entities/NSwagGenerated"
import { SessionInfo, PageAccess } from "../auth/personSessionData"
import Textarea from "primevue/textarea"
import Dialog from "primevue/dialog"
import AutoComplete, { AutoCompleteCompleteEvent } from "primevue/autocomplete"
import InputText from "primevue/inputtext"
import Button from "primevue/button"
import PageLabel from "./pageLabel.vue"
import * as Session from "../auth/personSessionData"
import { useToast } from "primevue/usetoast"

import MultiSelect from "primevue/multiselect"
import { unknown } from "zod/v4"

const apiClient = new ApiClient(import.meta.env.VITE_API_URL?.toString())
const saveAttempted = ref<boolean>(false)
const selectedPersons = ref<Person[] | undefined>([] as Person[])
const filteredPersons = ref<Person[] | undefined>([] as Person[])
const selectedSkills = ref<string[] | null>([] as string[])
const selectedSkill = ref<Screen | undefined | null>(null)

const filteredSkills = ref<Screen[] | null>([] as Screen[])
const selectableScreens = ref<Screen[] | undefined>([] as Screen[])
const actualSubProjectName = ref<string | undefined>(undefined)
const subProject = ref(new SubProject())
let subProjectAndCollaborators = new SubProjectCollaboratorPut()
const pageEditText = ref<string | undefined>(undefined)
let toast = useToast()
const props = defineProps({
	subProject: { type: SubProject, required: true },
	projectId: { type: String, required: false },
	multipleSelection: { type: Boolean, required: true },
	subProjectsList: { type: Array as PropType<SubProjectForTable[]>, default: () => [], required: true },
})
const emit = defineEmits<{
	(e: "cancel"): void
	(e: "save"): void
	(e: "deleted"): void
}>()

onMounted(async () => {
	// Wait for selectableScreens to load before setting selected values
	await fillSelectableScreens()
	console.log("Mounted SubProjectEdit.vue", props.subProject.id)
	if (props.subProject.id === undefined) {
		console.log("Mounted SubProjectEdit.vue1111", props.subProject.id)
		pageEditText.value = "Create Sub Project"
		subProject.value.id = self.crypto.randomUUID()
		subProject.value.isActive = true
		subProject.value.subProjectOwner = Session.SessionInfo.currentUser().id
		selectedPersons.value = [] as Person[]
		selectedSkills.value = null
	} else {
		pageEditText.value = "Modify Sub Project"
		subProject.value = props.subProject
		selectedPersons.value = addFullName(props.subProject.subProjectCollaborator)
		if (props.multipleSelection) {
			// Map the selected screen IDs from subProjectDefaultScreen
			// Since MultiSelect now uses optionValue="id", we store IDs directly
			selectedSkills.value = (subProject.value.subProjectDefaultScreen
				?.map((el) => el.screen?.id)
				.filter((id): id is string => id !== undefined && id !== null) ?? []) as string[]
		} else {
			selectedSkill.value =
				subProject.value.subProjectDefaultScreen?.length && subProject.value.subProjectDefaultScreen?.length > 0
					? subProject.value.subProjectDefaultScreen[0].screen
					: null
		}
	}

	actualSubProjectName.value = props.subProject.subProjectName
})
const personLabel = (person: Person) => {
	return `${person.firstName} ${person.lastName}`
}
function checkIfPersonIsPresent(event: any) {
	if (selectedPersons.value === undefined) return
	let isPresent = selectedPersons.value.filter((person: any) => person.id === event.value.id)
	if (isPresent.length > 1) {
		toast.add({
			severity: "warn",
			summary: "The selected collaborator is already in the collaborator list for this Sub Project",
			detail: `Select another collaborator`,
			life: 3000,
		})
		selectedPersons.value.splice(-1, 1)
	}
}
function checkIfSkillAreaIsPresent() {
	// With optionValue="id" and dataKey="id", PrimeVue handles duplicate prevention automatically
	// This function is kept for backward compatibility but no longer needs to check duplicates
	// The MultiSelect component will prevent duplicate IDs from being selected
}
function addFullName(personList: any) {
	let personTrureList = Array<Person>()
	personList.forEach((single: any) => {
		single.person.fullName = single.person.firstName + " " + single.person.lastName
		personTrureList.push(single.person)
	})
	return personTrureList
}

function testFields() {
	if (subProject.value.subProjectName == undefined) {
		toast.add({
			severity: "error",
			summary: "Error",
			detail: "You must assign a name to a sub project",
			life: 2500,
		})
		saveAttempted.value = true
		return false
	} else {
		if (props.subProjectsList != null && props.subProjectsList.length > 0) {
			//for new sub project
			if (props.subProject.id === undefined) {
				let subProjectIndex = props.subProjectsList.findIndex((a) => a.subProjectName == subProject.value.subProjectName)
				if (subProjectIndex > -1) {
					toast.add({
						severity: "error",
						summary: "Error",
						detail: "Same sub project name already exists",
						life: 2500,
					})
					saveAttempted.value = true
					return false
				}
			}
			//for existing sub project
			else {
				let subProjectIndex = props.subProjectsList.findIndex(
					(a) => a.subProjectName == subProject.value.subProjectName && a.id != props.subProject.id
				)
				if (subProjectIndex > -1) {
					toast.add({
						severity: "error",
						summary: "Error",
						detail: "Same sub project name already exists",
						life: 2500,
					})
					saveAttempted.value = true
					return false
				}
			}
		}
	}

	return true
}
function save() {
	if (!testFields()) return

	if (props.multipleSelection && selectedSkills.value) {
		// Convert selected IDs back to Screen objects
		subProjectAndCollaborators.subProjectDefaultScreens =
			selectableScreens.value?.filter((screen) => selectedSkills.value?.includes(screen.id!)) ?? []
	} else {
		subProjectAndCollaborators.subProjectDefaultScreens = []
		if (selectedSkill.value) subProjectAndCollaborators.subProjectDefaultScreens?.push(selectedSkill.value)
	}

	subProjectAndCollaborators.collaborators = selectedPersons.value ?? ([] as Person[])
	subProjectAndCollaborators.subProject = subProject.value
	subProjectAndCollaborators.subProject.projectId = props.projectId

	apiClient.subProjectPost(subProjectAndCollaborators).then((res) => {
		emit("save")
	})
}

async function fillSelectableScreens() {
	const el = await apiClient.screenForClientAlls()
	selectableScreens.value = []
	el.forEach((singleEl: ScreenTabs) => {
		if (singleEl.screen.screenName !== "Assessment Details") selectableScreens.value.push(singleEl.screen)
	})
	//selectableScreens.value = el
}

function searchPerson(event: AutoCompleteCompleteEvent) {
	apiClient.filterPersonForCollaborators(event.query).then((res) => {
		filteredPersons.value = res
	})
}
function searchSkill(event: { query: { trim: () => { (): any; new (): any; length: any }; toLowerCase: () => any } }) {
	apiClient.screenForClient(event.query).then((res: ScreenTabs[]) => {
		res.forEach((single) => {
			single.screen = []
			single.screen.screenName = single.screen.screenName
		})
		filteredSkills.value = res
	})
}
</script>
<style scoped></style>
